import React, { useContext, useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  Label,
  Form,
  FormFeedback,
  Button,
  Alert,
  Progress,
} from 'reactstrap';
import BreadCrumb from '../Components/Common/BreadCrumb';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import Dropzone from 'react-dropzone';
import { CSSTransition } from 'react-transition-group';
import { AboutUsContext } from '../context/AboutUsContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AboutUsForm = () => {
  document.title = 'مدیریت درباره ما | فات 90';

  const navigate = useNavigate();
  const { aboutUs, loading, error, uploadProgress, fetchAboutUs, addAboutUs, editAboutUs } =
    useContext(AboutUsContext);
  const [isEditMode, setIsEditMode] = useState(false);
  const [contentId, setContentId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showDropzone, setShowDropzone] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);

  // اعتبارسنجی فرم
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      AboutText: aboutUs?.aboutText || '',
      OurStory: aboutUs?.ourStory || '',
      SuccessfulTransactions: aboutUs?.successfulTransactions || 0,
      YearsOfExperience: aboutUs?.yearsOfExperience || 0,
      SatisfactionRate: aboutUs?.satisfactionRate || 0,
      Image: null,
    },
    validationSchema: Yup.object().shape({
      AboutText: Yup.string()
        .required('لطفاً متن درباره ما را وارد کنید')
        .max(5000, 'متن درباره ما باید حداکثر 5000 کاراکتر باشد'),
      OurStory: Yup.string()
        .required('لطفاً داستان ما را وارد کنید')
        .max(5000, 'داستان ما باید حداکثر 5000 کاراکتر باشد'),
      SuccessfulTransactions: Yup.number()
        .required('لطفاً تعداد تراکنش‌های موفق را وارد کنید')
        .min(0, 'تعداد تراکنش‌ها باید مثبت یا صفر باشد'),
      YearsOfExperience: Yup.number()
        .required('لطفاً سال‌های تجربه را وارد کنید')
        .min(0, 'سال‌های تجربه باید مثبت یا صفر باشد'),
      SatisfactionRate: Yup.number()
        .required('لطفاً نرخ رضایت را وارد کنید')
        .min(0, 'نرخ رضایت باید مثبت یا صفر باشد')
        .max(100, 'نرخ رضایت باید حداکثر 100 باشد'),
      ...(isEditMode
        ? {}
        : {
          Image: Yup.mixed()
            .required('لطفاً یک تصویر انتخاب کنید')
            .test('fileType', 'فقط فایل‌های jpg، jpeg، png یا webp مجاز هستند', (value) =>
              value ? ['image/jpeg', 'image/png', 'image/webp'].includes(value.type) : true
            )
            .test('fileSize', 'حجم فایل باید کمتر از 5MB باشد', (value) =>
              value ? value.size <= 300 * 1024 : true
            ),

        }),
    }),
    onSubmit: async (values) => {
      try {
        const aboutUsDto = {
          AboutText: values.AboutText,
          OurStory: values.OurStory,
          SuccessfulTransactions: parseInt(values.SuccessfulTransactions),
          YearsOfExperience: parseInt(values.YearsOfExperience),
          SatisfactionRate: parseFloat(values.SatisfactionRate),
          Image: values.Image,
        };

        if (isEditMode) {
          await editAboutUs(contentId, aboutUsDto);
          setShowDropzone(false);
          setCurrentImageUrl(aboutUsDto.Image ? null : aboutUs.imagePath); // به‌روزرسانی URL تصویر
        } else {
          await addAboutUs(aboutUsDto);
          setIsEditMode(true);
          setContentId(aboutUs?.id);
        }

        setSelectedFile(null);
        validation.setFieldValue('Image', null);
        validation.setTouched({ ...validation.touched, Image: false });
        validation.setErrors({ ...validation.errors, Image: undefined });
        setTimeout(() => navigate('/dashboard'), 2000);
      } catch (err) {
        // خطاها توسط AboutUsContext مدیریت می‌شن
      }
    },
  });

  // دریافت اطلاعات در بارگذاری اولیه
  useEffect(() => {
    fetchAboutUs();
  }, [fetchAboutUs]);

  // به‌روزرسانی حالت ویرایش و URL تصویر
  useEffect(() => {
    if (aboutUs) {
      setIsEditMode(true);
      setContentId(aboutUs.id);
      setCurrentImageUrl(aboutUs.imagePath);
    } else {
      setIsEditMode(false);
      setContentId(null);
      setCurrentImageUrl(null);
    }
  }, [aboutUs]);

  // مدیریت آپلود فایل
  const handleDrop = async (acceptedFiles, rejectedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      await validation.setFieldValue('Image', file, true);
      validation.setTouched({ ...validation.touched, Image: true });
      validation.setErrors({ ...validation.errors, Image: undefined });
    }

    if (rejectedFiles.length > 0) {
      setSelectedFile(null);
      await validation.setFieldValue('Image', null, true);
      validation.setTouched({ ...validation.touched, Image: true });

      const rejectedFile = rejectedFiles[0];
      const errorCodes = rejectedFile.errors.map((err) => err.code);

      if (errorCodes.includes('file-too-large')) {
        validation.setErrors({ ...validation.errors, Image: 'حجم فایل باید کمتر از 5MB باشد' });
      } else if (errorCodes.includes('file-invalid-type')) {
        validation.setErrors({ ...validation.errors, Image: 'فقط فایل‌های jpg، jpeg یا png مجاز هستند' });
      } else {
        validation.setErrors({ ...validation.errors, Image: 'فایل نامعتبر است' });
      }
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="مدیریت درباره ما" pageTitle="مدیریت" />
        <Row>
          <Col lg={8}>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <Card>
                <CardBody>
                  {error && <Alert color="danger">{error}</Alert>}
                  {aboutUs && !error && (
                    <Alert color="success">اطلاعات درباره ما با موفقیت بارگذاری شد</Alert>
                  )}

                  <div className="mb-3">
                    <Label htmlFor="about-text-input">درباره ما</Label>
                    <Input
                      type="textarea"
                      id="about-text-input"
                      name="AboutText"
                      rows="5"
                      value={validation.values.AboutText}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={validation.touched.AboutText && !!validation.errors.AboutText}
                    />
                    <FormFeedback>{validation.errors.AboutText}</FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="our-story-input">داستان ما</Label>
                    <Input
                      type="textarea"
                      id="our-story-input"
                      name="OurStory"
                      rows="5"
                      value={validation.values.OurStory}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={validation.touched.OurStory && !!validation.errors.OurStory}
                    />
                    <FormFeedback>{validation.errors.OurStory}</FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="successful-transactions-input">تراکنش‌های موفق</Label>
                    <Input
                      type="number"
                      id="successful-transactions-input"
                      name="SuccessfulTransactions"
                      value={validation.values.SuccessfulTransactions}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        validation.touched.SuccessfulTransactions &&
                        !!validation.errors.SuccessfulTransactions
                      }
                    />
                    <FormFeedback>{validation.errors.SuccessfulTransactions}</FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="years-of-experience-input">سال‌های تجربه</Label>
                    <Input
                      type="number"
                      id="years-of-experience-input"
                      name="YearsOfExperience"
                      value={validation.values.YearsOfExperience}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        validation.touched.YearsOfExperience && !!validation.errors.YearsOfExperience
                      }
                    />
                    <FormFeedback>{validation.errors.YearsOfExperience}</FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="satisfaction-rate-input">نرخ رضایت (%)</Label>
                    <Input
                      type="number"
                      id="satisfaction-rate-input"
                      name="SatisfactionRate"
                      step="0.1"
                      value={validation.values.SatisfactionRate}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        validation.touched.SatisfactionRate && !!validation.errors.SatisfactionRate
                      }
                    />
                    <FormFeedback>{validation.errors.SatisfactionRate}</FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label>تصویر</Label>
                    {isEditMode && currentImageUrl && !showDropzone && (
                      <>
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}${currentImageUrl}`}
                          alt="تصویر درباره ما"
                          className="w-100"
                          style={{ maxHeight: '200px', objectFit: 'cover' }}
                        />
                        <Button
                          color="secondary"
                          size="sm"
                          className="mt-2"
                          onClick={() => setShowDropzone(true)}
                        >
                          تغییر تصویر
                        </Button>
                      </>
                    )}

                    <CSSTransition in={showDropzone || !isEditMode} timeout={300} classNames="fade" unmountOnExit>
                      <div>
                        <Dropzone
                          onDrop={handleDrop}
                          accept={{
                            'image/jpeg': ['.jpg', '.jpeg'],
                            'image/png': ['.png'],
                            'image/webp': ['.webp']
                          }}
                          maxFiles={1}
                          maxSize={300 * 1024} // 300KB
                          multiple={false}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div className="dropzone dz-clickable" {...getRootProps()}>
                              <div className="dz-message needsclick">
                                <div className="mb-3">
                                  <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                                </div>
                                <h5>تصویر را اینجا بکشید یا کلیک کنید (jpg, jpeg, png, webp)</h5>
                              </div>
                              <input {...getInputProps()} />
                            </div>
                          )}
                        </Dropzone>
                        {isEditMode && (
                          <Button
                            color="light"
                            size="sm"
                            className="mt-2"
                            onClick={() => {
                              setShowDropzone(false);
                              setSelectedFile(null);
                              validation.setFieldValue('Image', null);
                              validation.setTouched({ ...validation.touched, Image: false });
                              validation.setErrors({ ...validation.errors, Image: undefined });
                            }}
                          >
                            لغو
                          </Button>
                        )}
                      </div>
                    </CSSTransition>

                    {selectedFile && !validation.errors.Image && (
                      <div className="mt-2">
                        <p>
                          <strong>فایل انتخاب‌شده:</strong> {selectedFile.name} (
                          {(selectedFile.size / 1024).toFixed(1)} KB)
                        </p>
                      </div>
                    )}

                    {validation.touched.Image && validation.errors.Image && (
                      <FormFeedback className="d-block">{validation.errors.Image}</FormFeedback>
                    )}
                  </div>

                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <Progress animated color="primary" value={uploadProgress} className="mb-3">
                      {uploadProgress}%
                    </Progress>
                  )}

                  <div className="text-end">
                    <Button
                      type="submit"
                      color="primary"
                      disabled={loading || (uploadProgress > 0 && uploadProgress < 100)}
                    >
                      {isEditMode ? 'به‌روزرسانی' : 'ثبت'}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Form>
          </Col>
        </Row>
        <ToastContainer closeButton={false} limit={1} />
      </Container>
    </div>
  );
};

export default AboutUsForm;